<template>
  <div
    class="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-7 2xl:grid-cols-4 gap-4 p-4"
  >
    <div
      class="xl:col-span-2 2xl:col-span-1 rounded-xl space-y-6 lg:space-y-10"
    >
      <div class="shadow-md sm:rounded-lg overflow-hidden">
        <div
          class="grid grid-cols-3 gap-2 bg-neutral-900/60 uppercase text-sm text-gray-400"
        >
          <div class="px-2 py-2">#ID</div>
          <div class="px-2 py-2">Username</div>
          <div class="px-2 py-2">Created at</div>
        </div>
        <div
          v-for="user in this.users"
          :key="user.id"
          class="grid grid-cols-3 gap-2 justify-start items-center text-sm text-gray-300 bg-neutral-700/30 border-b border-gray-700 cursor-pointer hover:bg-neutral-800"
          @click="this.toggleUser(user.id)"
        >
          <div class="px-2 py-1.5">{{ user.id }}</div>
          <div class="px-2 py-1.5">{{ user.username }}</div>
          <div class="px-2 py-1.5 flex justify-between items-center gap-4">
            <span>
              {{ new Date(user.created_at).toISOString().slice(0, 10) }}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4"
              :class="{
                'rotate-180': this.activeUser === user.id,
              }"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m4.5 15.75 7.5-7.5 7.5 7.5"
              />
            </svg>
          </div>
          <div v-if="this.activeUser === user.id" class="col-span-3 pl-5">
            <div class="bg-neutral-700 overflow-hidden">
              <div
                class="grid grid-cols-3 gap-2 bg-neutral-700/60 uppercase text-xs text-gray-400"
              >
                <div class="px-2 py-2">Network</div>
                <div class="px-2 py-2">Total Wallet</div>
                <div></div>
              </div>
              <div
                v-for="wallet in this.wallets"
                :key="wallet.id"
                class="grid grid-cols-3 gap-2 justify-start items-center text-xs text-gray-200 bg-neutral-700/30 border-b border-gray-700 cursor-pointer hover:bg-neutral-800/30 hover:text-blue-500"
                @click="this.selectNetwork(wallet, $event)"
                :class="{
                  'bg-neutral-800/30 text-blue-500':
                    'network' in this.networkWallet &&
                    wallet.network === this.networkWallet.network,
                }"
              >
                <div class="px-2 py-1.5 capitalize">{{ wallet.network }}</div>
                <div class="px-2 py-1.5">{{ wallet.data.length }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="'network' in this.networkWallet"
      class="lg:col-span-2 xl:col-span-5 2xl:col-span-3 grid grid-cols-5 gap-4"
    >
      <div
        class="col-span-3 lg:h-[calc(100vh-150px)] space-y-3 overflow-y-auto"
      >
        <div
          v-for="wallet in this.networkWallet.data"
          :key="wallet.address"
          class="bg-neutral-900/40 rounded-xl border border-gray-600 p-4"
        >
          <div
            class="flex flex-wrap justify-between items-center gap-4 xl:gap-6 2xl:gap-8"
          >
            <div class="text-sm flex items-center gap-2">
              <div class="font-bold">{{ wallet.name }}</div>
              <div class="text-sm hover:underline hover:text-blue-500">
                {{ wallet.address }}
              </div>
              <copy :value="wallet.address" />
              <copy :value="wallet.privateKey">
                <template #icon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-5 text-gray-500"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
                    />
                  </svg>
                </template>
              </copy>
            </div>
            <div class="flex justify-between items-center">
              <div class="text-sm gap-1 flex items-center">
                <div class="font-bold text-red-400">
                  {{ balanceFormat(this.balanceNative(wallet.address)) }}
                </div>
                <div>{{ this.symbolNative }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-2">
        <div>
          <label class="block text-sm mb-1 font-semibold uppercase">
            Recipient address
          </label>
          <input
            type="text"
            v-model="recipientAddress"
            class="block p-2 w-full text-sm bg-transparent rounded-xl border border-gray-500 focus:outline-none focus:ring-0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import server from "@/utils/server";
import { mapGetters, mapState } from "vuex";
import Copy from "@/components/Copy.vue";
import Tooltip from "@/components/Tooltip.vue";
import { Connection, LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import { ethers } from "ethers";

export default {
  name: "DashboardView",
  // eslint-disable-next-line vue/no-unused-components
  components: { Tooltip, Copy },
  data() {
    return {
      users: [],
      activeUser: 0,
      networkWallet: {},
      wallets: [],
      recipientAddress: "",
    };
  },
  computed: {
    ...mapGetters("app", ["balanceFormat"]),
    ...mapState("auth", ["token"]),
    ...mapGetters("network", ["chainList"]),
    // eslint-disable-next-line vue/return-in-computed-property
    symbolNative() {
      if ("network" in this.networkWallet) {
        switch (this.networkWallet["network"]) {
          case "solana":
            return "SOL";
          case "ethereum":
            return "ETH";
          case "bsc":
            return "BNB";
          case "base":
            return "ETH";
          default:
            break;
        }
      }
      return "?";
    },
  },
  methods: {
    balanceNative(address) {
      if ("network" in this.networkWallet) {
        const wallet = this.networkWallet.data.find(
          (wallet) => wallet.address === address
        );
        return wallet && wallet.balanceNative !== undefined
          ? wallet.balanceNative
          : 0;
      }
      return 0;
    },
    async getListUser() {
      try {
        const response = await server.post("/dashboard/user/list", {
          token: this.token,
        });
        if (response.status === 200) {
          this.users = response.data;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async toggleUser(userId) {
      try {
        this.networkWallet = {};
        if (this.activeUser === userId) {
          this.activeUser = 0;
        } else {
          const response = await server.post("/dashboard/user/wallet", {
            token: this.token,
            userId,
          });
          if (response.status === 200) {
            this.wallets = response.data.map((item) => ({
              ...item,
              data: JSON.parse(item.data),
            }));
            this.activeUser = userId;
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    selectNetwork(wallet, event) {
      this.networkWallet = wallet;
      this.fetchBalance();
      event.stopPropagation();
    },
    fetchBalance() {
      switch (this.networkWallet.network) {
        case "solana":
          this.fetchBalanceSolana();
          break;
        default:
          this.fetchBalanceEthereum();
          break;
      }
    },
    async fetchBalanceSolana() {
      const connection = new Connection(
        this.chainList.solana.rpcUrl,
        "confirmed"
      );
      const wallets = this.networkWallet.data;
      for (let i = 0; i < wallets.length; i++) {
        const wallet = wallets[i];
        const balance = await connection.getBalance(
          new PublicKey(wallets[i].address)
        );
        wallet.balanceNative = balance / LAMPORTS_PER_SOL;
        wallets[i] = wallet;
      }
      this.networkWallet.data = wallets;
    },
    async fetchBalanceEthereum() {
      let rpcUrl;
      switch (this.networkWallet["network"]) {
        case "ethereum":
          rpcUrl = this.chainList.ethereum.rpcUrl;
          break;
        case "bsc":
          rpcUrl = this.chainList.bsc.rpcUrl;
          break;
        case "base":
          rpcUrl = this.chainList.base.rpcUrl;
          break;
      }
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      const wallets = this.networkWallet.data;
      for (let i = 0; i < wallets.length; i++) {
        const wallet = wallets[i];
        let balance = await provider.getBalance(wallets[i].address);
        balance = Number(ethers.utils.formatEther(balance));
        wallet.balanceNative = balance;
        wallets[i] = wallet;
      }
      this.networkWallet.data = wallets;
    },
  },
  beforeMount() {
    this.getListUser();
  },
};
</script>
